'use client';

import React from 'react';

import {
  GlobalPreloader,
  Loading,
  NoSSR,
  SynchronizationInfo,
  Redirect,
  SynchronizationInfoContainer,
  SectionWithTitle,
} from 'components';
import { styled } from 'core/styles';
import { useRouteUtils } from 'routes';
import { MarketStatsPageData, getMarketAssetsPageData } from 'api/selectors/pages';
import { useServerData } from 'utils/react';
import { ProductSlug, VersionSlug, NetworkSlug } from 'domain/types';
import { LiqLevelsSection } from 'features/liq-levels';

import { TotalBalanceWidget } from '../widgets';
import { PriceCharts, TotalsTable } from './components';

export type MarketStatsProps = {
  productSlug: ProductSlug;
  versionSlug: VersionSlug;
  network: NetworkSlug;
  marketId: string;
  data: string;
};

export function MarketAssets({
  productSlug,
  versionSlug,
  network,
  marketId,
  data,
}: MarketStatsProps) {
  const pageDataRD = useServerData(
    () => getMarketAssetsPageData({ productSlug, versionSlug, network, marketId }),
    [marketId, network, productSlug, versionSlug],
    data,
  );

  return (
    <Loading data={pageDataRD} loader={<GlobalPreloader />}>
      {pageData => (
        <MarketContent
          productSlug={productSlug}
          versionSlug={versionSlug}
          marketId={marketId}
          network={network}
          {...pageData}
        />
      )}
    </Loading>
  );
}

function MarketContent({
  productSlug,
  versionSlug,
  marketId,
  network,
  marketData,
  protocolInfo,
  marketTokens,
  maxCollateralToken,
}: Omit<MarketStatsProps, 'data'> & MarketStatsPageData) {
  const { redirectTo } = useRouteUtils();

  if (!marketData) {
    return (
      <Redirect
        href={
          redirectTo.protocolStats({ productSlug, versionSlug, network: protocolInfo.network }).href
        }
      />
    );
  }

  return (
    <>
      <TotalBalanceWidget balance={marketData.balance} balanceDayAgo={marketData.balanceDayAgo} />
      <SynchronizationInfoContainer>
        <SynchronizationInfo
          timestamp={marketData.updatedAt}
          hasOraclePrice={marketData.hasOraclePrice}
          hasUnknownPrice={marketData.hasUnknownPrice}
        />
      </SynchronizationInfoContainer>

      <Content>
        {!!marketData.marketStatsData.length && (
          <SectionWithTitle title="Market stats">
            <TotalsTable data={marketData.marketStatsData} />
          </SectionWithTitle>
        )}
        <LiqLevelsSection
          productSlug={productSlug}
          versionSlug={versionSlug}
          networkSlug={network}
          marketVid={marketId}
          tokens={marketTokens}
          defaultToken={maxCollateralToken}
        />
        <NoSSR>
          <PriceCharts
            productSlug={productSlug}
            versionSlug={versionSlug}
            network={network}
            tokens={marketData.tokens}
          />
        </NoSSR>
      </Content>
    </>
  );
}

const Content = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 15,

  [theme.breakpoints.up('tabletLG')]: {
    gap: 30,
  },
}));
